import { FC, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { merge, pick } from "lodash-es";

import { editCurrentUser } from "Auth/state";
import { AUTH_ACTIONS } from "Auth/types";
import { useStateActionMeta } from "Auth/utils";
import { UserHotelPreferencesForm } from "Bands/components";
import {
  IBandUserHotelPreferences,
  UserBooleanAttribute,
  UserElevatorProximity,
  UserRoomLocation,
} from "Bands/types";
import { useProfileContext } from "Auth/contexts";
import { useIsUserPreferenceFormSubmitted } from "Bands/utils";
import { IAPIState } from "shared/types";

interface Props {
  handleClose: (v: boolean) => void;
}

export const EditUserHotelPreferencesForm: FC<Props> = ({ handleClose }) => {
  const dispatch = useDispatch();
  const { userAttributes } = useProfileContext();

  const { status, error } = useStateActionMeta(AUTH_ACTIONS.editCurrentUser) || {};

  const handleSubmit = (data: IBandUserHotelPreferences) => {
    const { smokingRoom, accessibleRoom, roomLocation, elevatorProximity } =
      data.hotelPreferences || {};

    const cleanData: IBandUserHotelPreferences = merge({}, data, {
      hotelPreferences: {
        smokingRoom: smokingRoom === UserBooleanAttribute.yes ? true : false,
        accessibleRoom: accessibleRoom === UserBooleanAttribute.yes ? true : false,
        roomLocation: roomLocation === UserRoomLocation.none ? null : roomLocation,
        elevatorProximity:
          elevatorProximity === UserElevatorProximity.none ? null : elevatorProximity,
      },
    });

    setFormSubmitted(true);
    dispatch(editCurrentUser(cleanData));
  };

  const defaultValues = useMemo(() => {
    const { smokingRoom, accessibleRoom, roomLocation, elevatorProximity } =
      userAttributes?.hotelPreferences || {};

    return merge({}, pick(userAttributes, "hotelPreferences"), {
      hotelPreferences: {
        smokingRoom: smokingRoom ? UserBooleanAttribute.yes : UserBooleanAttribute.no,
        accessibleRoom: accessibleRoom ? UserBooleanAttribute.yes : UserBooleanAttribute.no,
        roomLocation: roomLocation === null ? UserRoomLocation.none : roomLocation,
        elevatorProximity:
          elevatorProximity === null ? UserElevatorProximity.none : elevatorProximity,
      },
    });
  }, [userAttributes]);

  const { isFormSubmitted, setFormSubmitted } = useIsUserPreferenceFormSubmitted();

  useEffect(() => {
    if (status === IAPIState.succeeded) {
      handleClose(false);
    }
  });

  return (
    <UserHotelPreferencesForm
      defaultValues={defaultValues}
      handleSubmit={handleSubmit}
      apiError={isFormSubmitted ? error : undefined}
      error={isFormSubmitted ? !!error : undefined}
      apiStatus={isFormSubmitted ? status : undefined}
    />
  );
};
